// 离线优先 + 自动同步 统一入口
// 数据规则 (布尔并集): (group,op) 任一方完成即完成; 云端删除已确认进度会回写本地(撤销)
// dirty = "这条本地完成记录尚未确认上传到云端"
// syncNow: 上传本地 dirty → 拉云端并集合并 → 更新 lastSyncAt → 重算 learnable
// triggerAutoSync: 幂等并发锁, 供首页/联网恢复/完成学习 调用, 失败时置 failed

import { api } from './api';
import {
    getDirtyProgress, clearDirty, getAllProgress, putProgress,
    putWords, setMeta, setLearnableGroups, getAllWordsFromIDB, getWordsCount,
} from './db';

const OPS = ['study', 'test', 'review1', 'review2', 'review3'];

// ---------- 同步状态机 (供 Home / SyncNotifications 共享) ----------
// state: 'idle' | 'syncing' | 'synced' | 'failed'
// detail: { phase: 'upload' | 'merge', dirty, message, pushed, pulled }
const listeners = new Set();
let syncStatus = { state: 'idle', detail: null };

export function getSyncStatus() {
    return syncStatus;
}

export function subscribeSyncStatus(fn) {
    listeners.add(fn);
    return () => listeners.delete(fn);
}

function setSyncStatus(state, detail) {
    syncStatus = { state, detail: detail || null };
    listeners.forEach(fn => { try { fn(syncStatus); } catch (e) {} });
}

// ---------- 核心同步 ----------
export async function syncNow() {
    const result = { pushed: 0, pulled: 0, warmed: 0, pushedEvents: [], pulledEvents: [] };

    // 阶段1: 上传本地 dirty (严格按 dirty 推送, 非 dirty 视为已确认)
    const dirty = await getDirtyProgress();
    setSyncStatus('syncing', { phase: 'upload', dirty: dirty.length });
    if (dirty.length > 0) {
        const events = dirty.map(p => ({
            new_group_id: p.new_group_id,
            group_id: p.group_id,
            op_type: p.op_type,
            completed_at: p.completed_at,
            duration_sec: p.duration_sec || 0,
        }));
        const r = await api.syncPush(events);
        result.pushed = r.data.updated || events.length;
        result.pushedEvents = events;
        // 仅清"已确认上传"的 dirty (云端最终存在该 new_group_id/op 即清)
        await Promise.all(dirty.map(p => clearDirty(p.new_group_id, p.op_type)));
    }

    // 阶段2: 拉云端进度, 布尔并集合并 (只补不覆盖, 不动本地已有)
    setSyncStatus('syncing', { phase: 'merge', dirty: 0 });
    const pullResp = await api.syncPull();
    const serverEvents = pullResp.data.events || [];
    const localAll = await getAllProgress();
    for (const ev of serverEvents) {
        const key = `${ev.new_group_id}:${ev.op_type}`;
        const local = localAll.find(p => p.key === key);
        if (!local) {
            // 本地缺失: 直接补入(布尔并集)
            await putProgress(ev.new_group_id, ev.op_type, ev.completed_at, false, ev.duration_sec || 0);
            result.pulled++;
            result.pulledEvents.push(ev);
        } else {
            // 本地已存在: 若云端日期更新(如服务端修正了时区误记),
            // 用云端日期覆盖本地显示时间, 让首页进度与云端一致(LWW, 只升不降, 按日期比较)
            const localDate = (local.completed_at || '').slice(0, 10);
            const serverDate = (ev.completed_at || '').slice(0, 10);
            if (serverDate > localDate) {
                await putProgress(ev.new_group_id, ev.op_type, ev.completed_at, false, ev.duration_sec || 0);
                result.pulled++;
                result.pulledEvents.push(ev);
            }
        }
    }

    // 阶段2b: 不再自动删除本地进度。
    // 原逻辑会把"服务端 pull 中不存在的本地非 dirty 记录"删掉，用于"撤销"回写；
    // 但布尔并集语义下，服务端 pull 缺失≠被撤销(可能是推送时序/服务端异常)，
    // 这会静默丢掉已完成的进度(如 Review2)。回归离线方案原始设计「不删本地」，
    // 同步只做布尔并集(只补不删)，保证任何已完成的 (group,op) 永不丢失。
    // 如需撤销，应在 /api/sync/pull 显式返回"已删除清单"再据此删除，而非按缺失推断。

    // 阶段3: 首次 warmup 词库 (仅 IDB 空时, 避免每次重拉 1852 词)
    if ((await getWordsCount()) === 0) {
        const allWords = await api.getAllWords();
        await putWords(allWords.data);
        result.warmed = allWords.data.length;
    }

    // 阶段4: 更新 lastSyncAt + 重算可学习范围
    await setMeta('lastSyncAt', new Date().toISOString());
    await recomputeLearnableGroups();

    // 阶段5: 后台预取离线资源(思维导图+语音), 不阻塞同步完成
    prefetchLearnableAssets().catch(() => {});

    setSyncStatus('synced', { pushed: result.pushed, pulled: result.pulled });
    return result;
}

let autoSyncRunning = false;

// 幂等自动同步: 并发调用只会跑一次; 失败置 failed (本地进度仍保留)
export async function triggerAutoSync() {
    if (autoSyncRunning) return;
    autoSyncRunning = true;
    try {
        await syncNow();
    } catch (e) {
        setSyncStatus('failed', { message: (e && e.message) || String(e) });
    } finally {
        autoSyncRunning = false;
    }
}

// 重算可学习范围(split: 以 new_group_id 为单元): 未完全完成的拆分组 + 最后完成组之后的 5 组
export async function recomputeLearnableGroups() {
    const allWords = await getAllWordsFromIDB();
    if (!allWords || allWords.length === 0) {
        await setLearnableGroups([]);
        return;
    }
    const allGroups = Array.from(new Set(allWords.map(w => String(w.new_group_id))))
        .filter(Boolean)
        .sort((a, b) => Number(a) - Number(b));
    const progress = await getAllProgress();
    const completedByGroup = {};
    progress.forEach(p => {
        const g = String(p.new_group_id);
        if (!completedByGroup[g]) completedByGroup[g] = new Set();
        completedByGroup[g].add(p.op_type);
    });
    const isComplete = (g) => {
        const ops = completedByGroup[g];
        return !!ops && OPS.every(op => ops.has(op));
    };
    const learnable = new Set();
    let maxComplete = -1;
    allGroups.forEach(g => {
        if (isComplete(g)) {
            if (Number(g) > maxComplete) maxComplete = Number(g);
        } else {
            learnable.add(g);
        }
    });
    // 最后完成组后的 5 组
    if (maxComplete >= 0) {
        const idx = allGroups.indexOf(String(maxComplete));
        for (let i = 1; i <= 5; i++) {
            if (idx + i < allGroups.length) learnable.add(allGroups[idx + i]);
        }
    }
    await setLearnableGroups(Array.from(learnable).sort((a, b) => Number(a) - Number(b)));
}

// ---------- 预取离线资源（思维导图 + 语音）----------
// 范围: 未完成组(有记录但未集齐5项) ∪ 未开始组中最前面10组
// 仅在线执行, 后台非阻塞; 去重直接查 SW 缓存 (caches.match), 不依赖 meta 标记,
// 避免"SW 未接管时预取未真正落盘却被误标为已完成"导致后续跳过。
const PREFETCH_CONCURRENCY = 6;
const PREFETCH_NOT_STARTED_AHEAD = 10;
const PREFETCH_OPS = ['study', 'test', 'review1', 'review2', 'review3'];

// mode:'no-cors' 使请求键与 <img>/<Audio> 完全一致, 确保 SW cache-first 命中
function prefetchUrl(url) {
    return fetch(url, { mode: 'no-cors' }).catch(() => {});
}

function computePrefetchGroups(allWords, progress) {
    const allGroups = Array.from(new Set(allWords.map(w => String(w.new_group_id))))
        .filter(Boolean)
        .sort((a, b) => Number(a) - Number(b));
    const opsByGroup = {};
    progress.forEach(p => {
        const g = String(p.new_group_id);
        if (!opsByGroup[g]) opsByGroup[g] = new Set();
        opsByGroup[g].add(p.op_type);
    });
    const isComplete = (g) => !!opsByGroup[g] && PREFETCH_OPS.every(op => opsByGroup[g].has(op));
    const incomplete = allGroups.filter(g => opsByGroup[g] && !isComplete(g));   // 已开始但未完成
    const notStarted = allGroups.filter(g => !opsByGroup[g]);                     // 完全无记录
    const firstNotStarted = notStarted.slice(0, PREFETCH_NOT_STARTED_AHEAD);
    return Array.from(new Set([...incomplete, ...firstNotStarted])).sort((a, b) => Number(a) - Number(b));
}

export async function prefetchLearnableAssets() {
    if (typeof navigator === 'undefined' || !navigator.onLine) return;
    if (!('caches' in window)) return;
    const allWords = await getAllWordsFromIDB();
    if (!allWords || allWords.length === 0) return;
    const progress = await getAllProgress();
    const target = computePrefetchGroups(allWords, progress);
    if (target.length === 0) return;

    const byGroup = {};
    allWords.forEach(w => {
        const g = String(w.new_group_id);
        if (!byGroup[g]) byGroup[g] = [];
        byGroup[g].push(w);
    });

    // 收集需预取的 URL: 每组首词思维导图 + 每个词语音
    const urls = [];
    for (const g of target) {
        const words = byGroup[g] || [];
        if (words.length === 0) continue;
        const picId = words[0].picture_id;
        if (picId) urls.push(api.pictureUrl(picId));
        for (const w of words) {
            if (w.word) urls.push(api.ttsUrl(w.word));
        }
    }

    // 准确去重: 只预取 SW 缓存里尚不存在的 URL (避免 meta 误标导致永久跳过)
    const todo = [];
    for (const u of urls) {
        const hit = await caches.match(u).catch(() => undefined);
        if (!hit) todo.push(u);
    }
    if (todo.length === 0) return;

    // 分片并发, 限制同时请求数, 单条失败忽略
    for (let i = 0; i < todo.length; i += PREFETCH_CONCURRENCY) {
        const batch = todo.slice(i, i + PREFETCH_CONCURRENCY).map(u => prefetchUrl(u));
        await Promise.allSettled(batch);
    }
}

const OP_LABEL = { study: '学习', test: '测试', review1: '复习I', review2: '复习II', review3: '复习III' };

// 格式化同步结果为可读提示 (保留, 用于失败时/手动诊断)
export function formatSyncResult(r) {
    const fmt = (e) => `${(e.completed_at || '').slice(0, 10)} Group${e.group_id} ${OP_LABEL[e.op_type] || e.op_type}`;
    const lines = [];
    if (r.pushed > 0) {
        lines.push(`推送 ${r.pushed} 条进度：`);
        r.pushedEvents.forEach(e => lines.push('  ' + fmt(e)));
    }
    if (r.pulled > 0) {
        lines.push(`拉取 ${r.pulled} 条进度：`);
        r.pulledEvents.forEach(e => lines.push('  ' + fmt(e)));
    }
    if (r.pushed === 0 && r.pulled === 0) {
        lines.push('无新进度，已是最新');
    }
    return '同步完成：\n' + lines.join('\n');
}
